import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PermissionGate } from "@/components/permission/permission-gate";
import { EmptyRow } from "@/features/oversight/oversight-ui";
import { formatTransferAmount } from "@/features/transfers/transfer-amount";
import type { Transfer } from "@/types";

export function RecentTransfers({ transfers }: { transfers: Transfer[] }) {
  const isEmpty = transfers.length === 0;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">Recent operations</h2>
        <Link to="/transfers" className="text-xs font-semibold text-primary hover:underline">
          View all →
        </Link>
      </div>

      <div className="flex-1 overflow-hidden rounded-xl border border-line bg-bg">
        <table className="w-full text-sm">
          <thead className="bg-surface text-xs uppercase tracking-[0.05em] text-muted">
            <tr>
              <th className="px-3 py-1.5 text-left font-semibold">Amount</th>
              <th className="px-3 py-1.5 text-left font-semibold">From → To</th>
              <th className="px-3 py-1.5 text-left font-semibold">Asset</th>
              <th className="px-3 py-1.5 text-left font-semibold">Status</th>
              <th className="px-3 py-1.5 text-right font-semibold">Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {isEmpty ? (
              // The column headers stay so an empty tile still says what it will
              // hold, and the row carries the way to put something in it rather
              // than a bare "no data" box a customer cannot act on.
              <EmptyRow colSpan={5}>
                <div>No recent operations.</div>
                <PermissionGate permission="transfer:create">
                  <Link to="/transfers/new" className="mt-2 inline-block font-semibold text-primary underline">
                    New transfer
                  </Link>
                </PermissionGate>
              </EmptyRow>
            ) : (
              transfers.map((t) => (
                <tr key={t.id} className="transition-colors hover:bg-surface">
                  <td className="px-3 py-2">
                    <Link to={`/transfers/${t.id}`} className="flex items-center gap-1.5 font-semibold text-ink hover:text-primary">
                      {t.direction === "send" ? <ArrowUpRight className="size-3.5 text-info" /> : <ArrowDownLeft className="size-3.5 text-success" />}
                      {formatTransferAmount(t.amount, t.asset)}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <div className="max-w-[180px] truncate text-xs text-muted">{t.route}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted">{t.asset}</td>
                  <td className="px-3 py-2">
                    <Badge variant={t.statusVariant} size="sm" dot={false}>
                      {t.statusLabel}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-muted">{t.created}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
